/**
 * VetBuddy API client for the appointment form (sections/appointment-form.liquid)
 * and the service booking drawer (assets/available-service-booking.js).
 *
 * Calls the VetBuddy endpoints and returns the same response shapes the old
 * /apps/zg/ proxy returned, so the forms work unchanged:
 *   getClient(phone)                     -> like /apps/zg/api/client?phonenumber=
 *   getStaffSlots(clinicId, staffId, d)  -> like /apps/zg/api/client/staff?clinicid=&staffid=&availabilitydate=
 *   getReasons(speciesName)              -> like /apps/zg/api/client/reasons?speciesname=
 *   getStaff(clinicId, staffCategory)    -> like /apps/zg/api/client/staff/clinic/{id}?staffcategory=
 * Booking submission (/apps/zg/api/client/appointment) has no VetBuddy endpoint and is unchanged.
 *
 * If VetBuddy cannot be reached (network, CORS, mixed content, timeout, 5xx),
 * the call falls back to the old /apps/zg/ proxy.
 */
(function () {
  'use strict';

  if (window.ZiglyVetBuddy) return;

  // VetBuddy's middleware only lets https://zigly.com call it from a browser, so on
  // `shopify theme dev` (127.0.0.1) calls go through a local dev proxy on port 9494
  // that forwards them server-side. Every other host calls VetBuddy directly.
  const IS_LOCAL_DEV = /^(127\.0\.0\.1|localhost)$/.test(window.location.hostname);
  const BASE_URL = IS_LOCAL_DEV
    ? 'http://127.0.0.1:9494/api/vetbuddy'
    : 'https://rktlagqrxj.execute-api.ap-south-1.amazonaws.com/api/vetbuddy';
  const LEGACY_BASE_URL = '/apps/zg/';
  const REQUEST_TIMEOUT_MS = 15000;
  const RETRY_DELAY_MS = 800;

  // The clinic API needs a city, so the clinic list is built from these cities.
  const CLINIC_CITIES = [
    'Delhi', 'New Delhi', 'Gurugram', 'Noida', 'Ahmedabad', 'Jaipur', 'Chandigarh', 'Panchkula',
    'Zirakpur', 'Ludhiana', 'Amritsar', 'Pune', 'Hyderabad', 'Bengaluru', 'Lucknow', 'Indore',
    'Surat', 'Dehradun',
  ];

  // VetBuddy "Error" values that mean an empty result rather than a failure.
  const NOT_FOUND_ERROR = /no data found|not found/i;

  class VetBuddyUnavailableError extends Error {}

  let vetbuddyOffline = false;
  let clinicsPromise = null;

  // Empty XML nodes arrive as {}; turn them (and null) into ''.
  function text(value) {
    if (value === null || value === undefined || typeof value === 'object') return '';
    return String(value).trim();
  }

  // Single results arrive as an object, several as an array.
  function list(value) {
    if (value === null || value === undefined || value === '') return [];
    return Array.isArray(value) ? value : [value];
  }

  async function fetchJson(url) {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS) : null;
    try {
      const response = await fetch(url, controller ? { signal: controller.signal } : undefined);
      const data = await response.json().catch(() => null);
      return { response, data };
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  async function vetbuddyGetOnce(path, params) {
    const query = new URLSearchParams(params).toString();
    let result;
    try {
      result = await fetchJson(`${BASE_URL}/${path}?${query}`);
    } catch (error) {
      const unreachable = new VetBuddyUnavailableError(`VetBuddy ${path} unreachable: ${error.message}`);
      unreachable.unreachable = true;
      throw unreachable;
    }

    const { response, data } = result;
    if (!response.ok || !data || (typeof data.code === 'number' && data.code >= 400)) {
      throw new VetBuddyUnavailableError(`VetBuddy ${path} failed: ${(data && data.message) || `HTTP ${response.status}`}`);
    }
    if (data.Error) {
      if (NOT_FOUND_ERROR.test(text(data.Error))) return null;
      throw new VetBuddyUnavailableError(`VetBuddy ${path} error: ${text(data.Error)}`);
    }
    return data;
  }

  /** GET a VetBuddy endpoint; null means "not found". Retries once before giving up. */
  async function vetbuddyGet(path, params) {
    if (vetbuddyOffline) throw new VetBuddyUnavailableError('VetBuddy is unreachable from this page');
    try {
      return await vetbuddyGetOnce(path, params);
    } catch (error) {
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      try {
        return await vetbuddyGetOnce(path, params);
      } catch (retryError) {
        // Blocked by the browser (CORS / mixed content): skip VetBuddy for the rest of the page.
        if (retryError.unreachable) vetbuddyOffline = true;
        throw retryError;
      }
    }
  }

  // Same behaviour as the old fetch + response.json(): errors reach the form's own catch,
  // which shows its usual "Unable to verify..." / "Error loading slots" messages.
  async function legacyGet(path) {
    try {
      const response = await fetch(`${LEGACY_BASE_URL}${path}`);
      return await response.json();
    } catch (error) {
      console.error('[ZiglyVetBuddy] /apps/zg/ proxy failed too:', error.message);
      throw error;
    }
  }

  function warnFallback(error) {
    console.warn('[ZiglyVetBuddy] Using /apps/zg/ proxy instead:', error.message);
  }

  // --------------------------------------------------------------------------
  // Clinics (clinic API, one call per city)
  // --------------------------------------------------------------------------

  function getAllClinics() {
    if (!clinicsPromise) {
      let failedCities = 0;
      clinicsPromise = Promise.all(
        CLINIC_CITIES.map((city) =>
          vetbuddyGet('clinic', { city })
            .then((data) => list(data && data.Clinics && data.Clinics.Clinic))
            .catch((error) => {
              // One failing city must not break the whole lookup
              failedCities++;
              console.warn(`[ZiglyVetBuddy] Clinics for ${city} not loaded:`, error.message);
              return [];
            })
        )
      )
        .then((results) => {
          if (failedCities === CLINIC_CITIES.length) {
            throw new VetBuddyUnavailableError('VetBuddy clinic API failed for every city');
          }
          const seen = new Set();
          return results.flat()
            .map((clinic) => ({
              ClinicId: text(clinic.ClinicId),
              ClinicName: text(clinic.ClinicName),
              ClinicCity: text(clinic.ClinicCity),
              ClinicState: text(clinic.ClinicState),
              ClinicAddress1: text(clinic.ClinicAddress1),
              ClinicAddress2: text(clinic.ClinicAddress2),
              ClinicZip: text(clinic.ClinicZip),
              ClinicTelephone: text(clinic.ClinicTelephone),
            }))
            .filter((clinic) => clinic.ClinicId && !seen.has(clinic.ClinicId) && seen.add(clinic.ClinicId));
        })
        .catch((error) => {
          clinicsPromise = null;
          throw error;
        });
    }
    return clinicsPromise;
  }

  // --------------------------------------------------------------------------
  // Patients (patients API, for species and birth date)
  // --------------------------------------------------------------------------

  async function getPatientRecord(clinicId, listPatient) {
    const patientId = text(listPatient.PatientID);
    const basic = {
      PatientID: patientId,
      PatientName: text(listPatient.PatientName),
      BirthDate: '',
      Species: { SpeciesName: text(listPatient.Species) },
      Status: text(listPatient.Status),
    };

    try {
      const data = await vetbuddyGet('patients', { clinicid: clinicId, patientid: patientId });
      const patient = list(data && data.Patients && data.Patients.Patient)[0];
      if (!patient) return basic;
      return {
        PatientID: text(patient.PatientID) || patientId,
        PatientName: text(patient.PatientName) || basic.PatientName,
        BirthDate: text(patient.BirthDate),
        Species: {
          SpeciesId: text(patient.Species && patient.Species.SpeciesId),
          SpeciesName: text(patient.Species && patient.Species.SpeciesName) || basic.Species.SpeciesName,
        },
        Breed: {
          BreedId: text(patient.Breed && patient.Breed.BreedId),
          BreedName: text(patient.Breed && patient.Breed.BreedName),
        },
        Gender: {
          GenderId: text(patient.Gender && patient.Gender.GenderId),
          GenderName: text(patient.Gender && patient.Gender.GenderName),
        },
        Status: text(patient.Status) || basic.Status,
        DeceasedDate: text(patient.DeceasedDate),
      };
    } catch (error) {
      // The pet list already has name and species, so details are optional.
      return basic;
    }
  }

  /** False for a pet VetBuddy marks Deceased or Inactive (or gives a DeceasedDate): it can't be booked. */
  function isBookablePatient(patient) {
    const status = patient.Status.toLowerCase();
    return status !== 'deceased' && status !== 'inactive' && !patient.DeceasedDate;
  }

  // --------------------------------------------------------------------------
  // Public API (old proxy response shapes)
  // --------------------------------------------------------------------------

  /**
   * Client, pets and clinics for a phone number (client-patient + patients + clinic APIs).
   * Resolves like the old proxy: { responseCode: 0, status: 'success', data: { client, patients, clinics } }.
   */
  async function getClient(phoneNumber) {
    try {
      const data = await vetbuddyGet('client-patient', {
        phonenumber: phoneNumber,
        action: 'clientpatient',
        status: 'Active',
      });
      const clients = list(data && data.Clients && data.Clients.Client);
      if (!clients.length) {
        return { responseCode: 1, status: 'error', message: 'Client not found' };
      }

      const primary = clients[0];
      const client = {};
      Object.keys(primary).forEach((key) => {
        if (key !== 'Patients') client[key] = typeof primary[key] === 'object' ? primary[key] : text(primary[key]);
      });
      ['ClientID', 'ClinicId', 'ClinicName', 'FirstName', 'LastName', 'Email', 'MobilePhone', 'HomePhone'].forEach((key) => {
        client[key] = text(primary[key]);
      });

      const seen = new Set();
      const listPatients = clients.flatMap((entry) =>
        list(entry.Patients && entry.Patients.Patient).map((patient) => ({ clinicId: text(entry.ClinicId), patient }))
      ).filter(({ patient }) => {
        const id = text(patient.PatientID);
        return id && !seen.has(id) && seen.add(id);
      });

      const [records, clinics] = await Promise.all([
        Promise.all(listPatients.map(({ clinicId, patient }) => getPatientRecord(clinicId, patient))),
        getAllClinics(),
      ]);
      // Only pets that can still be booked reach the "Choose your pet" dropdowns.
      const patients = records.filter(isBookablePatient);

      console.info('[ZiglyVetBuddy] Client loaded from VetBuddy API');
      return { responseCode: 0, status: 'success', data: { client, patients, clinics } };
    } catch (error) {
      warnFallback(error);
      return legacyGet(`api/client?phonenumber=${encodeURIComponent(phoneNumber)}`);
    }
  }

  /**
   * Clinic list on its own (clinic API), for callers that need clinics even when the
   * phone number has no VetBuddy client yet - a first-time customer still picks a clinic.
   * Resolves like getClient's clinics: { responseCode: 0, status: 'success', data: { clinics } }.
   */
  async function getClinics() {
    try {
      return { responseCode: 0, status: 'success', data: { clinics: await getAllClinics() } };
    } catch (error) {
      console.warn('[ZiglyVetBuddy] Clinic list unavailable:', error.message);
      return { responseCode: 1, status: 'error', message: 'Clinics unavailable', data: { clinics: [] } };
    }
  }

  /**
   * Time slots for a staff member on a date, availabilityDate as 'MM/DD/YYYY' (staff-availability API).
   * Resolves like the old proxy: { responseCode: 0, status: 'success', data: [{ StaffID, Slots, ... }] }.
   */
  async function getStaffSlots(clinicId, staffId, availabilityDate) {
    try {
      const data = await vetbuddyGet('staff-availability', {
        clinicid: clinicId,
        staffid: staffId,
        availabilitydatefrom: availabilityDate,
        availabilitydateto: availabilityDate,
      });
      const availability = data && data.StaffRotas && data.StaffRotas.StaffAvailability;
      const rows = list(availability && availability.Dates && availability.Dates.RotaDate)
        .flatMap((rotaDate) => list(rotaDate.Staffs))
        .flatMap((entry) => list(entry && entry.Staff ? entry.Staff : entry))
        .map((member) => ({
          StaffID: text(member.StaffID),
          StaffName: text(member.StaffName),
          StaffCategoryName: text(member.StaffCategory && member.StaffCategory.StaffCategoryName),
          Slots: text(member.Slots),
        }))
        .filter((member) => member.StaffID === String(staffId));

      return { responseCode: 0, status: 'success', data: rows };
    } catch (error) {
      warnFallback(error);
      return legacyGet(
        `api/client/staff?clinicid=${clinicId}&staffid=${staffId}&availabilitydate=${encodeURIComponent(availabilityDate)}`
      );
    }
  }

  // VetBuddy pages this endpoint and caps a page at REASONS_PAGE_SIZE rows.
  const REASONS_PAGE_SIZE = 100;
  const REASONS_MAX_PAGES = 40;

  /** Every reason row for a species, following the endpoint's pagination. */
  async function getAllReasonPages(speciesName) {
    const params = (page) => ({ speciesname: speciesName, page, pagesize: REASONS_PAGE_SIZE });
    const rowsOf = (data) => list(data && data.ReasonForVisits && data.ReasonForVisits.ReasonForVisit);

    const first = await vetbuddyGet('reason-for-visit', params(1));
    const rows = rowsOf(first);
    if (!rows.length) return [];

    const attributes = (first.ReasonForVisits && first.ReasonForVisits['@attributes']) || {};
    const totalPages = Math.min(parseInt(attributes.total_pages, 10) || 1, REASONS_MAX_PAGES);
    if (totalPages <= 1) return rows;

    // Pages are independent, so fetch them together rather than one after another.
    const rest = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, index) =>
        vetbuddyGet('reason-for-visit', params(index + 2))
          .then(rowsOf)
          .catch((error) => {
            // A missing page costs us those services, not the whole list.
            console.warn(`[ZiglyVetBuddy] Reasons page ${index + 2} for ${speciesName} not loaded:`, error.message);
            return [];
          })
      )
    );

    return rows.concat(...rest);
  }

  /**
   * Reasons for visit for a species name, e.g. 'Canine', 'Feline' or 'Canine (Dog)' (reason-for-visit API).
   * Resolves like the old proxy: { responseCode: 0, status: 'success', data: [{ ReasonForVisitID, ReasonForVisitName, TimeTakenMins }] }.
   * Every page is fetched: a product identifies its service by ReasonForVisitID, and those
   * IDs are spread across the whole list, so a single page would silently lose most of them.
   * Only speciesname is sent: VetBuddy files reasons under species IDs that differ from the
   * breed/gender IDs (Canine reasons are species 86), so adding speciesid=1 returns nothing.
   */
  async function getReasons(speciesName) {
    try {
      const rows = await getAllReasonPages(text(speciesName));
      const seen = new Set();
      const reasons = rows
        .filter((reason) => !text(reason.Status) || text(reason.Status).toLowerCase() === 'active')
        .map((reason) => ({
          ReasonForVisitID: text(reason.ReasonForVisitID),
          ReasonForVisitName: text(reason.ReasonForVisitName),
          TimeTakenMins: text(reason.TimeTakenMins),
          Category: text(reason.Category && reason.Category.Category),
          SpeciesName: text(reason.Species && reason.Species.Species),
        }))
        .filter((reason) => reason.ReasonForVisitID && !seen.has(reason.ReasonForVisitID) && seen.add(reason.ReasonForVisitID));

      return reasons.length
        ? { responseCode: 0, status: 'success', data: reasons }
        : { responseCode: 1, status: 'error', message: 'No reasons found', data: [] };
    } catch (error) {
      warnFallback(error);
      return legacyGet(`api/client/reasons?speciesname=${encodeURIComponent(speciesName)}`);
    }
  }

  /**
   * Active doctors or groomers at a clinic, staffCategory 'doctor' or 'groomer' (staff API).
   * Resolves like the old proxy: { responseCode: 0, status: 'success', data: [{ StaffID, FirstName, LastName, StaffCategoryName }] }.
   */
  async function getStaff(clinicId, staffCategory) {
    try {
      const data = await vetbuddyGet('staff', { status: 'active', clinicid: clinicId, staffcategory: staffCategory });
      const staff = list(data && data.Staffs && data.Staffs.Staff)
        .filter((member) => !text(member.Status) || text(member.Status).toLowerCase() === 'active')
        .map((member) => ({
          StaffID: text(member.StaffID),
          FirstName: text(member.FirstName),
          LastName: text(member.LastName),
          StaffCategoryID: text(member.StaffCategoryID),
          StaffCategoryName: text(member.StaffCategoryName),
          ClinicId: text(member.ClinicId),
          ClinicName: text(member.ClinicName),
        }))
        .filter((member) => member.StaffID);

      return staff.length
        ? { responseCode: 0, status: 'success', data: staff }
        : { responseCode: 1, status: 'error', message: 'No staff found', data: [] };
    } catch (error) {
      warnFallback(error);
      return legacyGet(`api/client/staff/clinic/${clinicId}?staffcategory=${staffCategory}`);
    }
  }

  // --------------------------------------------------------------------------
  // Writes (client, patient, appointment)
  // --------------------------------------------------------------------------

  /** POST a JSON body to a VetBuddy endpoint. Throws with the API's own message on failure. */
  async function vetbuddyPost(path, body) {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS) : null;
    let response;
    let data;
    try {
      response = await fetch(`${BASE_URL}/${path}`, Object.assign({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }, controller ? { signal: controller.signal } : {}));
      data = await response.json().catch(() => null);
    } catch (error) {
      throw new VetBuddyUnavailableError(`VetBuddy ${path} unreachable: ${error.message}`);
    } finally {
      if (timer) clearTimeout(timer);
    }

    if (!response.ok || !data || data.success === false) {
      throw new VetBuddyUnavailableError((data && data.message) || `VetBuddy ${path} failed: HTTP ${response.status}`);
    }
    return data;
  }

  // The write endpoints answer with the created record under one of these keys.
  function createdId(data, keys) {
    const sources = [data, data && data.data, data && data.result].filter(Boolean);
    for (const source of sources) {
      for (const key of keys) {
        const value = text(source[key]);
        if (value) return value;
      }
    }
    return '';
  }

  /**
   * Register a client (pet parent) in VetBuddy. Phone (10 digits) and firstName are required.
   * An existing phone is not an error: VetBuddy answers with that client ({ existing: true, clientId }).
   * Resolves to { responseCode: 0, status: 'success', data: { clientId, raw } }.
   */
  async function createClient(details) {
    try {
      const data = await vetbuddyPost('client', {
        phone: text(details.phone),
        firstName: text(details.firstName),
        lastName: text(details.lastName),
        email: text(details.email),
        clinicName: text(details.clinicName),
        address1: text(details.address1),
        city: text(details.city),
        zip: text(details.zip),
        state: text(details.state),
      });
      return {
        responseCode: 0,
        status: 'success',
        data: { clientId: createdId(data, ['clientId', 'ClientID', 'clientID', 'id']), raw: data },
      };
    } catch (error) {
      console.warn('[ZiglyVetBuddy] Client not created:', error.message);
      return { responseCode: 1, status: 'error', message: error.message };
    }
  }

  /**
   * Register a pet against a client. clientId, petName and species are required.
   * Resolves to { responseCode: 0, status: 'success', data: { patientId, raw } }.
   */
  async function createPatient(details) {
    try {
      const data = await vetbuddyPost('patient', {
        clientId: text(details.clientId),
        phone: text(details.phone),
        petName: text(details.petName),
        species: text(details.species),
        clinicName: text(details.clinicName),
        breed: text(details.breed),
        gender: text(details.gender),
        neutered: details.neutered === true,
        birthDate: text(details.birthDate),
      });
      return {
        responseCode: 0,
        status: 'success',
        data: { patientId: createdId(data, ['patientId', 'PatientID', 'patientID', 'id']), raw: data },
      };
    } catch (error) {
      // VetBuddy refuses a second pet with the same name and species for a client
      // ("Patient #82763 With Name And Species Is Already Active For Client #65984").
      // That pet is the one being booked, so use its ID rather than failing.
      const existing = /Patient #(\d+) With Name And Species Is Already Active/i.exec(error.message || '');
      if (existing) {
        return { responseCode: 0, status: 'success', data: { patientId: existing[1], existing: true, raw: null } };
      }
      console.warn('[ZiglyVetBuddy] Pet not created:', error.message);
      return { responseCode: 1, status: 'error', message: error.message };
    }
  }

  /**
   * Book an appointment. The endpoint requires clientId, patientId, staffId, clinicName,
   * appointmentType and date, plus either timeSlot ('15:00-15:30') or startTime + endTime.
   * Resolves to { responseCode: 0, status: 'success', data: { appointmentId, raw } }.
   */
  async function createAppointment(details) {
    try {
      const body = {
        clientId: text(details.clientId),
        patientId: text(details.patientId),
        staffId: text(details.staffId),
        clinicName: text(details.clinicName),
        appointmentType: text(details.appointmentType),
        date: text(details.date),
      };
      if (text(details.timeSlot)) {
        body.timeSlot = text(details.timeSlot);
      } else {
        body.startTime = text(details.startTime);
        body.endTime = text(details.endTime);
      }
      if (text(details.reason)) body.reason = text(details.reason);
      if (text(details.crmAppointmentId)) body.crmAppointmentId = text(details.crmAppointmentId);

      const data = await vetbuddyPost('appointment', body);
      return {
        responseCode: 0,
        status: 'success',
        data: { appointmentId: createdId(data, ['appointmentId', 'AppointmentID', 'appointmentID', 'id']), raw: data },
      };
    } catch (error) {
      console.warn('[ZiglyVetBuddy] Appointment not created:', error.message);
      return { responseCode: 1, status: 'error', message: error.message };
    }
  }

  window.ZiglyVetBuddy = {
    getClient, getClinics, getStaffSlots, getReasons, getStaff,
    createClient, createPatient, createAppointment,
  };
})();
